"use client";

import * as React from "react";
import { CalendarCheck } from "lucide-react";

import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export function CalendarDropdown({
  className,
  value,
  onChange,
  disableFutureDates = false,
  minDate,
  placeholder = "Select date", // Default placeholder
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const [popoverWidth, setPopoverWidth] = React.useState(0);

  const getToday = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    try {
      const [year, month, day] = value.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date;
    } catch (error) {
      console.error("[CalendarDropdown] Error parsing date:", error);
      return undefined;
    }
  }, [value]);

  const maxDate = React.useMemo(() => {
    if (!disableFutureDates) {
      return undefined;
    }
    const today = getToday();
    return today;
  }, [disableFutureDates]);

  const minDateValue = React.useMemo(() => {
    if (!minDate) {
      return undefined;
    }

    if (minDate instanceof Date) {
      const normalized = new Date(minDate);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    }

    if (typeof minDate === "string") {
      const [year, month, day] = minDate.split("-").map(Number);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        const parsed = new Date(year, month - 1, day);
        parsed.setHours(0, 0, 0, 0);
        return parsed;
      }
    }

    return undefined;
  }, [minDate]);

  const disabledDates = React.useMemo(() => {
    const constraints = {};

    if (disableFutureDates && maxDate) {
      constraints.after = maxDate;
    }

    if (minDateValue) {
      constraints.before = minDateValue;
    }

    return Object.keys(constraints).length > 0 ? constraints : undefined;
  }, [disableFutureDates, maxDate, minDateValue]);

  React.useEffect(() => {
    if (triggerRef.current) {
      const updateWidth = () => {
        setPopoverWidth(triggerRef.current.offsetWidth);
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, []);

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            id="date"
            className="w-full justify-between font-normal h-[46px] hover:bg-theme-primary/10"
          >
            {dateValue ? dateValue.toLocaleDateString() : placeholder}
            <CalendarCheck className="h-5 w-5 text-theme-primary" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="overflow-hidden p-0"
          align="start"
          style={{ width: popoverWidth > 0 ? `${popoverWidth}px` : "auto" }}
        >
          <Calendar
            mode="single"
            selected={dateValue}
            captionLayout="dropdown"
            toDate={maxDate}
            fromDate={minDateValue}
            disabled={disabledDates}
            onSelect={(selectedDate) => {
              if (selectedDate) {
                if (disableFutureDates) {
                  const today = getToday();
                  const selectedDateNormalized = new Date(selectedDate);
                  selectedDateNormalized.setHours(0, 0, 0, 0);

                  if (selectedDateNormalized > today) {
                    return;
                  }
                }

                if (minDateValue) {
                  const selectedDateNormalized = new Date(selectedDate);
                  selectedDateNormalized.setHours(0, 0, 0, 0);

                  if (selectedDateNormalized < minDateValue) {
                    return;
                  }
                }
                const year = selectedDate.getFullYear();
                const month = String(selectedDate.getMonth() + 1).padStart(
                  2,
                  "0",
                );
                const day = String(selectedDate.getDate()).padStart(2, "0");
                const formattedDate = `${year}-${month}-${day}`;
                onChange?.(formattedDate);
              } else {
                onChange?.(undefined);
              }
              setOpen(false);
            }}
            className="w-full"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
